import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  Calendar, 
  Clock, 
  Settings,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle,
  User,
  AlertCircle,
  CalendarDays,
  LayoutGrid,
  X,
  MapPin,
  Mail,
  Phone
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const FULL_DAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

// Hours for week view (8h-19h)
const HOURS = Array.from({ length: 12 }, (_, i) => i + 8);

const FullCalendar = ({ theme, user, myCalendarOnly = false }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week'); // 'week' by default or 'month'
  const [appointments, setAppointments] = useState([]);
  const [recurringBlocks, setRecurringBlocks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [calendarEmbedUrl, setCalendarEmbedUrl] = useState('');
  const [useEmbedCalendar, setUseEmbedCalendar] = useState(false);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [respondingTo, setRespondingTo] = useState(null);
  const [proposedDate, setProposedDate] = useState('');
  const [proposedTime, setProposedTime] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [showLegend, setShowLegend] = useState(!myCalendarOnly); // Hide legend in myCalendarOnly mode
  const [visibleMembers, setVisibleMembers] = useState(myCalendarOnly && user?.id ? [user.id] : []); // In myCalendarOnly mode, filter to current user
  
  // Edit mode for appointments
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    requested_date: '',
    requested_time: '',
    subject: '',
    description: '',
    duration_minutes: 30
  });
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Drag and drop states
  const [draggedItem, setDraggedItem] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  
  // Client reschedule request states
  const [showRescheduleRequest, setShowRescheduleRequest] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({
    requested_date: '',
    requested_time: '',
    reason: ''
  });
  const [sendingReschedule, setSendingReschedule] = useState(false);
  
  const isClient = user?.role === 'client';

  // Quick RDV creation states
  const [showQuickRdvDialog, setShowQuickRdvDialog] = useState(false);
  const [quickRdvForm, setQuickRdvForm] = useState({
    date: '',
    time: '09:00',
    duration_minutes: 30,
    subject: '',
    description: '',
    appointment_type: 'meeting',
    client_id: '',
    project_id: '',
    custom_client_name: ''
  });
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [savingRdv, setSavingRdv] = useState(false);

  // Handler for clicking an empty cell to create a new appointment
  const handleCellClick = (date, hour) => {
    // Don't open if it's in the past
    const now = new Date();
    const cellDateTime = new Date(date);
    cellDateTime.setHours(hour, 0, 0, 0);
    if (cellDateTime < now) {
      // Allow creating appointments for today even if hour has passed
      if (date.toDateString() !== now.toDateString()) {
        toast.error("Impossible de créer un RDV dans le passé");
        return;
      }
    }
    
    // Format the date
    const dateStr = date.toISOString().split('T')[0];
    const timeStr = `${hour.toString().padStart(2, '0')}:00`;
    
    setQuickRdvForm({
      date: dateStr,
      time: timeStr,
      duration_minutes: 30,
      subject: '',
      description: '',
      appointment_type: 'meeting',
      client_id: '',
      project_id: '',
      custom_client_name: ''
    });
    setShowQuickRdvDialog(true);
  };

  // Create a new appointment
  const handleCreateQuickRdv = async () => {
    if (!quickRdvForm.subject.trim()) {
      toast.error('Veuillez saisir un sujet');
      return;
    }
    
    setSavingRdv(true);
    try {
      await api.post('/calendar/appointments/create', {
        client_id: quickRdvForm.client_id || null,
        account_manager_id: user?.id,
        date: quickRdvForm.date,
        time: quickRdvForm.time,
        duration_minutes: quickRdvForm.duration_minutes,
        appointment_type: quickRdvForm.appointment_type,
        subject: quickRdvForm.subject,
        description: quickRdvForm.description,
        project_id: quickRdvForm.project_id || null,
        custom_client_name: quickRdvForm.custom_client_name || null,
        send_invitation: !!quickRdvForm.client_id
      });
      
      toast.success('RDV créé avec succès');
      setShowQuickRdvDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error creating RDV:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSavingRdv(false);
    }
  };

  // Fetch clients and projects for dropdown
  const fetchClientsAndProjects = async () => {
    try {
      const [clientsRes, projectsRes] = await Promise.all([
        api.get('/users/', { params: { role: 'client' } }),
        api.get('/projects/')
      ]);
      setClients(clientsRes.data || []);
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error('Error fetching clients/projects:', error);
    }
  };

  // Fetch clients and projects when needed
  useEffect(() => {
    if (showQuickRdvDialog && clients.length === 0) {
      fetchClientsAndProjects();
    }
  }, [showQuickRdvDialog]);

  const isAgency = user?.role === 'agency';
  const isProjectManager = user?.role === 'project_manager';

  // Get week start (Monday)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  // Get month start
  const getMonthStart = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const weekStart = getWeekStart(currentDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    return date;
  });

  // Get calendar days for month view (including padding)
  const getMonthDays = () => {
    const monthStart = getMonthStart(currentDate);
    const startDay = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1; // Monday = 0
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    
    const days = [];
    
    // Add padding days from previous month
    for (let i = startDay - 1; i >= 0; i--) {
      const d = new Date(monthStart);
      d.setDate(d.getDate() - i - 1);
      days.push({ date: d, isCurrentMonth: false });
    }
    
    // Add current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(currentDate.getFullYear(), currentDate.getMonth(), i), isCurrentMonth: true });
    }
    
    // Add padding days for next month (to complete 6 rows)
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, i);
      days.push({ date: d, isCurrentMonth: false });
    }
    
    return days;
  };

  // Initialize visibleMembers when myCalendarOnly mode is enabled
  useEffect(() => {
    if (myCalendarOnly && user?.id) {
      setVisibleMembers([user.id]);
    }
  }, [myCalendarOnly, user?.id]);

  useEffect(() => {
    fetchData();
    loadCalendarSettings();
  }, [currentDate]);

  const loadCalendarSettings = async () => {
    try {
      const res = await api.get('/auth/me');
      // Ne pas activer l'embed par défaut - l'utilisateur doit explicitement le configurer
      // et il sera stocké dans les préférences locales
      const savedPref = localStorage.getItem('useGoogleCalendarEmbed');
      if (res.data?.google_calendar_embed_url && savedPref === 'true') {
        setCalendarEmbedUrl(res.data.google_calendar_embed_url);
        setUseEmbedCalendar(true);
      }
    } catch (e) {
      console.error('Error loading calendar settings');
    }
  };

  const saveCalendarSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/users/me/calendar-settings', {
        google_calendar_embed_url: calendarEmbedUrl
      });
      const useEmbed = !!calendarEmbedUrl;
      setUseEmbedCalendar(useEmbed);
      localStorage.setItem('useGoogleCalendarEmbed', useEmbed.toString());
      toast.success('Paramètres du calendrier enregistrés');
      setShowSettingsDialog(false);
    } catch (e) {
      console.error('Error saving settings:', e);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Determine if user is agency/manager to fetch all blocks
      const isAgencyUser = user?.role === 'agency' || user?.role === 'project_manager' || user?.role === 'customer_service';
      
      // Fetch appointments, recurring blocks (ALL for agency), and members
      const [appointmentsRes, blocksRes, membersRes] = await Promise.all([
        api.get('/calendar/all-events').catch(() => api.get('/calendar/appointments')),
        // For agency users, get ALL blocks including other members' personal blocks
        isAgencyUser 
          ? api.get('/calendar/recurring-blocks/all').catch(() => ({ data: [] }))
          : api.get('/calendar/recurring-blocks').catch(() => ({ data: [] })),
        api.get('/calendar/members').catch(() => ({ data: [] }))
      ]);
      
      // Set recurring blocks
      setRecurringBlocks(blocksRes.data || []);
      
      // If we got data from all-events, use the enriched appointments
      if (appointmentsRes.data?.appointments) {
        setAppointments(appointmentsRes.data.appointments || []);
        setTeamMembers(appointmentsRes.data.members || membersRes.data || []);
      } else {
        setAppointments(appointmentsRes.data || []);
        setTeamMembers(membersRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (view === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  const handleRespondToAppointment = async (appointmentId, action) => {
    try {
      const body = { action };
      
      if (action === 'propose_new_time') {
        if (!proposedDate || !proposedTime) {
          toast.error('Veuillez proposer une date et une heure');
          return;
        }
        body.proposed_date = proposedDate;
        body.proposed_time = proposedTime;
        body.message = responseMessage;
      } else if (action === 'decline') {
        body.message = responseMessage;
      }

      await api.post(`/calendar/appointments/${appointmentId}/respond`, body);
      toast.success(
        action === 'accept' ? 'RDV accepté' :
        action === 'propose_new_time' ? 'Nouvelle date proposée' : 'RDV décliné'
      );
      setShowAppointmentDialog(false);
      setSelectedAppointment(null);
      setRespondingTo(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la réponse');
    }
  };

  // Get appointments for a specific day
  const getAppointmentsForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(apt => apt.requested_date === dateStr);
  };

  // Get appointments for a specific hour slot
  const getAppointmentsForHour = (date, hour) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(apt => {
      if (apt.requested_date !== dateStr) return false;
      const aptHour = parseInt(apt.requested_time?.split(':')[0] || '0', 10);
      if (aptHour !== hour) return false;
      
      // Filter by visible members if any selected
      if (visibleMembers.length > 0) {
        return visibleMembers.includes(apt.assigned_to) || 
               visibleMembers.includes(apt.account_manager_id) ||
               visibleMembers.includes(apt.member_id);
      }
      return true;
    });
  };

  const pendingAppointments = appointments.filter(
    apt => apt.status === 'pending' && apt.account_manager_id === user?.id
  );

  const formatWeekRange = () => {
    const endOfWeek = new Date(weekStart);
    endOfWeek.setDate(weekStart.getDate() + 6);
    const opts = { day: 'numeric', month: 'short' };
    return `${weekStart.toLocaleDateString('fr-FR', opts)} - ${endOfWeek.toLocaleDateString('fr-FR', opts)} ${weekStart.getFullYear()}`;
  };

  const formatMonthYear = () => {
    return `${MONTHS_FR[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 border-yellow-300 text-yellow-800 hover:bg-yellow-200',
      accepted: 'bg-green-100 border-green-300 text-green-800 hover:bg-green-200',
      proposed_new_time: 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200',
      declined: 'bg-red-100 border-red-300 text-red-800 hover:bg-red-200',
      google: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
    };
    return colors[status] || colors.pending;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      accepted: 'Accepté',
      proposed_new_time: 'Nouvelle date proposée',
      declined: 'Décliné',
      google: 'Google Calendar'
    };
    return labels[status] || status;
  };

  const openAppointmentDetails = (apt) => {
    setSelectedAppointment(apt);
    setRespondingTo(null);
    setProposedDate('');
    setProposedTime('');
    setResponseMessage('');
    setIsEditing(false);
    setEditForm({
      requested_date: apt.requested_date || '',
      requested_time: apt.requested_time || '',
      subject: apt.subject || '',
      description: apt.description || '',
      duration_minutes: apt.duration_minutes || 30
    });
    setShowAppointmentDialog(true);
  };

  // Handle saving edited appointment
  const handleSaveAppointment = async () => {
    if (!selectedAppointment) return;
    
    setSavingEdit(true);
    try {
      await api.put(`/calendar/appointments/${selectedAppointment.id}`, {
        date: editForm.requested_date,
        time: editForm.requested_time,
        subject: editForm.subject,
        description: editForm.description,
        duration_minutes: editForm.duration_minutes
      });
      toast.success('RDV modifié');
      setIsEditing(false);
      setShowAppointmentDialog(false);
      setSelectedAppointment(null);
      fetchData();
    } catch (error) {
      console.error('Error saving appointment:', error);
      toast.error('Erreur lors de la modification');
    } finally {
      setSavingEdit(false);
    }
  };

  // Handle deleting appointment
  const handleDeleteAppointment = async () => {
    if (!selectedAppointment) return;
    if (!window.confirm('Supprimer ce RDV ?')) return;
    
    try {
      await api.delete(`/calendar/appointments/${selectedAppointment.id}`);
      toast.success('RDV supprimé');
      setShowAppointmentDialog(false);
      setSelectedAppointment(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id);
    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedItem(null);
    setDropTarget(null);
  };

  const handleDragOver = (e, date, hour) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const targetKey = `${date.toISOString().split('T')[0]}-${hour}`;
    setDropTarget(targetKey);
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = async (e, date, hour) => {
    e.preventDefault();
    setDropTarget(null);
    
    if (!draggedItem) return;
    
    const newDate = date.toISOString().split('T')[0];
    const newTime = `${hour.toString().padStart(2, '0')}:00`;
    
    // Check if it's a task or appointment
    if (draggedItem.is_task) {
      // Update task due date
      try {
        await api.put(`/tasks/${draggedItem.id}`, {
          due_date: newDate,
          due_time: newTime
        });
        toast.success('Tâche reprogrammée');
        fetchData();
      } catch (error) {
        console.error('Error updating task:', error);
        toast.error('Erreur lors du déplacement de la tâche');
      }
    } else {
      // Update appointment
      try {
        await api.put(`/calendar/appointments/${draggedItem.id}`, {
          date: newDate,
          time: newTime,
          subject: draggedItem.subject,
          description: draggedItem.description,
          duration_minutes: draggedItem.duration_minutes
        });
        toast.success('RDV reprogrammé');
        fetchData();
      } catch (error) {
        console.error('Error updating appointment:', error);
        toast.error('Erreur lors du déplacement du RDV');
      }
    }
    
    setDraggedItem(null);
  };

  // If using Google Calendar embed
  
  // Handle client reschedule request
  const handleRequestReschedule = async () => {
    if (!selectedAppointment) return;
    
    setSendingReschedule(true);
    try {
      await api.post(`/calendar/appointments/${selectedAppointment.id}/request-reschedule`, {
        requested_date: rescheduleForm.requested_date,
        requested_time: rescheduleForm.requested_time,
        reason: rescheduleForm.reason
      });
      toast.success('Demande de modification envoyée');
      setShowRescheduleRequest(false);
      setShowAppointmentDialog(false);
      setRescheduleForm({ requested_date: '', requested_time: '', reason: '' });
      fetchData();
    } catch (error) {
      console.error('Error requesting reschedule:', error);
      toast.error('Erreur lors de l\'envoi de la demande');
    } finally {
      setSendingReschedule(false);
    }
  };

  if (useEmbedCalendar && calendarEmbedUrl) {
    return (
      <Card className="card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" style={{ color: theme?.colors?.accent }} />
              Mon Agenda
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => setShowSettingsDialog(true)}>
              <Settings className="w-4 h-4 mr-2" />
              Paramètres
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <iframe 
            src={calendarEmbedUrl}
            style={{ border: 0, width: '100%', height: '600px' }}
            frameBorder="0"
            scrolling="no"
            title="Google Calendar"
          />
        </CardContent>
        
        {/* Settings Dialog */}
        <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Paramètres du Calendrier</DialogTitle>
              <DialogDescription>
                Gérez l'intégration de votre Google Calendar
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>URL d'intégration Google Calendar</Label>
                <Input
                  value={calendarEmbedUrl}
                  onChange={(e) => setCalendarEmbedUrl(e.target.value)}
                  placeholder="https://calendar.google.com/calendar/embed?src=..."
                  className="mt-1"
                />
              </div>
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setCalendarEmbedUrl('');
                  setUseEmbedCalendar(false);
                  localStorage.setItem('useGoogleCalendarEmbed', 'false');
                }}
              >
                Utiliser le calendrier intégré
              </Button>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={saveCalendarSettings} 
                disabled={savingSettings}
                style={{ backgroundColor: theme?.colors?.accent }}
              >
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>
    );
  }

  return (
    <Card className="card" data-testid="full-calendar">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" style={{ color: theme?.colors?.accent }} />
            Mon Agenda
            <a 
              href="/calendar" 
              className="text-xs text-slate-400 hover:text-slate-600 font-normal ml-2"
              title="Ouvrir l'agenda complet"
            >
              <ExternalLink className="w-3 h-3 inline" />
            </a>
          </CardTitle>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* View toggle */}
            <div className="flex items-center border rounded-lg overflow-hidden">
              <Button 
                variant={view === 'week' ? 'default' : 'ghost'} 
                size="sm" 
                className={cn("rounded-none h-8", view === 'week' && 'text-white')}
                style={view === 'week' ? { backgroundColor: theme?.colors?.accent } : {}}
                onClick={() => setView('week')}
              >
                <CalendarDays className="w-4 h-4 mr-1" />
                Semaine
              </Button>
              <Button 
                variant={view === 'month' ? 'default' : 'ghost'} 
                size="sm" 
                className={cn("rounded-none h-8", view === 'month' && 'text-white')}
                style={view === 'month' ? { backgroundColor: theme?.colors?.accent } : {}}
                onClick={() => setView('month')}
              >
                <LayoutGrid className="w-4 h-4 mr-1" />
                Mois
              </Button>
            </div>

            <Button variant="outline" size="sm" onClick={goToToday}>
              Aujourd'hui
            </Button>
            
            <div className="flex items-center border rounded-lg">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateDate(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium px-2 min-w-[140px] text-center">
                {view === 'week' ? formatWeekRange() : formatMonthYear()}
              </span>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateDate(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setShowSettingsDialog(true)}>
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Pending appointments banner */}
        {pendingAppointments.length > 0 && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-700 font-medium">
              <AlertCircle className="w-4 h-4" />
              {pendingAppointments.length} demande(s) de RDV en attente
            </div>
            <div className="mt-2 space-y-1">
              {pendingAppointments.slice(0, 3).map(apt => (
                <div 
                  key={apt.id}
                  className="flex items-center justify-between bg-white p-2 rounded cursor-pointer hover:bg-yellow-100 transition-colors"
                  onClick={() => openAppointmentDetails(apt)}
                >
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="font-medium">{apt.client_name}</span>
                    <span className="text-slate-500">{apt.requested_date} à {apt.requested_time}</span>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-xs">Répondre</Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Member Color Legend - Click to filter (hidden in myCalendarOnly mode) */}
        {teamMembers.length > 0 && showLegend && !myCalendarOnly && (
          <div className="bg-slate-50 p-3 rounded-lg mt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                Filtrer par membre {visibleMembers.length > 0 && `(${visibleMembers.length} sélectionné${visibleMembers.length > 1 ? 's' : ''})`}
              </span>
              <div className="flex items-center gap-1">
                {visibleMembers.length > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 px-2 text-xs text-blue-600"
                    onClick={() => setVisibleMembers([])}
                  >
                    Tous
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 text-xs"
                  onClick={() => setShowLegend(false)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {teamMembers.map(member => {
                const isSelected = visibleMembers.length === 0 || visibleMembers.includes(member.id);
                return (
                  <button
                    key={member.id} 
                    className={cn(
                      "flex items-center gap-1.5 px-2 py-1 rounded-full transition-all cursor-pointer border",
                      isSelected 
                        ? "bg-white shadow-sm" 
                        : "opacity-40 bg-slate-100"
                    )}
                    style={{ borderColor: isSelected ? member.calendar_color : 'transparent' }}
                    onClick={() => {
                      if (visibleMembers.length === 0) {
                        // First click: show only this member
                        setVisibleMembers([member.id]);
                      } else if (visibleMembers.includes(member.id)) {
                        // Already selected: remove from filter
                        const newVisible = visibleMembers.filter(id => id !== member.id);
                        setVisibleMembers(newVisible);
                      } else {
                        // Not selected: add to filter
                        setVisibleMembers([...visibleMembers, member.id]);
                      }
                    }}
                  >
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: member.calendar_color }}
                    />
                    <span className="text-xs text-slate-700 font-medium">{member.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        
        {!showLegend && teamMembers.length > 0 && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="mt-2 h-6 px-2 text-xs text-slate-500"
            onClick={() => setShowLegend(true)}
          >
            Afficher la légende
          </Button>
        )}
      </CardHeader>

      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : view === 'week' ? (
          /* WEEK VIEW WITH HOURS */
          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              {/* Header with days */}
              <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b bg-slate-50 sticky top-0 z-10">
                <div className="p-2 border-r text-xs text-slate-400 text-center">
                  GMT+1
                </div>
                {weekDays.map((date, idx) => {
                  const isToday = date.toDateString() === new Date().toDateString();
                  const dayAppointments = getAppointmentsForDay(date);
                  return (
                    <div 
                      key={idx} 
                      className={cn(
                        "p-2 text-center border-r last:border-r-0",
                        isToday && "bg-indigo-50"
                      )}
                    >
                      <div className="text-xs text-slate-500 uppercase font-medium">{DAYS_FR[idx]}</div>
                      <div className={cn(
                        "text-xl font-bold mt-0.5 w-8 h-8 mx-auto rounded-full flex items-center justify-center",
                        isToday ? "bg-indigo-600 text-white" : "text-slate-700"
                      )}>
                        {date.getDate()}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Time grid - Height increased for full day view (08h-19h) */}
              <div className="relative" style={{ minHeight: '600px' }}>
                {HOURS.map((hour) => (
                  <div key={hour} className="grid grid-cols-[60px_repeat(7,1fr)] border-b" style={{ minHeight: '50px' }}>
                    {/* Hour label */}
                    <div className="p-1 border-r text-xs text-slate-400 text-right pr-2 pt-0">
                      {hour.toString().padStart(2, '0')}:00
                    </div>
                    
                    {/* Day columns */}
                    {weekDays.map((date, dayIdx) => {
                      const isToday = date.toDateString() === new Date().toDateString();
                      const hourAppointments = getAppointmentsForHour(date, hour);
                      const cellKey = `${date.toISOString().split('T')[0]}-${hour}`;
                      const isDropTarget = dropTarget === cellKey;
                      
                      // Check for recurring blocks
                      const dayNames = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'];
                      const dayName = dayNames[date.getDay()];
                      const blockForThisHour = recurringBlocks.find(block => {
                        if (!block.days?.includes(dayName)) return false;
                        const blockStart = parseInt(block.start_time?.split(':')[0] || '0');
                        const blockEnd = parseInt(block.end_time?.split(':')[0] || '0');
                        return hour >= blockStart && hour < blockEnd;
                      });
                      
                      return (
                        <div 
                          key={dayIdx}
                          className={cn(
                            "border-r last:border-r-0 p-1 relative cursor-pointer hover:bg-slate-50/50 transition-colors",
                            isToday && "bg-indigo-50/30",
                            isDropTarget && "bg-blue-100 ring-2 ring-blue-400 ring-inset"
                          )}
                          style={blockForThisHour && !isDropTarget ? { backgroundColor: (blockForThisHour.color || '#94a3b8') + '15' } : {}}
                          onClick={(e) => {
                            // Only trigger if clicking on the cell itself, not on an appointment
                            if (e.target === e.currentTarget || e.target.closest('.calendar-cell-bg')) {
                              handleCellClick(date, hour);
                            }
                          }}
                          onDragOver={(e) => handleDragOver(e, date, hour)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, date, hour)}
                        >
                          {/* Clickable background layer */}
                          <div className="calendar-cell-bg absolute inset-0" />
                          
                          {/* Drop indicator */}
                          {isDropTarget && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                              <span className="text-xs font-medium text-blue-600 bg-white/80 px-2 py-1 rounded shadow">
                                Déposer ici
                              </span>
                            </div>
                          )}
                          
                          {/* Show recurring block label at start hour */}
                          {blockForThisHour && parseInt(blockForThisHour.start_time?.split(':')[0]) === hour && (
                            <div 
                              className="absolute inset-x-1 top-0 px-1.5 py-0.5 text-[10px] font-medium rounded-t z-5 truncate"
                              style={{ 
                                backgroundColor: blockForThisHour.color || '#94a3b8',
                                color: 'white'
                              }}
                            >
                              {blockForThisHour.title}
                            </div>
                          )}
                          
                          {hourAppointments.map((apt, aptIdx) => (
                            <div
                              key={apt.id}
                              draggable={apt.status !== 'pending'}
                              onDragStart={(e) => handleDragStart(e, apt)}
                              onDragEnd={handleDragEnd}
                              onClick={(e) => {
                                e.stopPropagation();
                                openAppointmentDetails(apt);
                              }}
                              className={cn(
                                "absolute left-1 right-1 p-1.5 rounded border text-xs cursor-grab transition-all shadow-sm hover:shadow-md z-10",
                                !apt.member_color && getStatusColor(apt.status),
                                apt.is_task && "border-dashed",
                                aptIdx > 0 && "mt-1",
                                draggedItem?.id === apt.id && "opacity-50"
                              )}
                              style={{ 
                                top: `${aptIdx * 28 + 4}px`,
                                minHeight: '24px',
                                ...(apt.member_color && {
                                  backgroundColor: apt.member_color + '20',
                                  borderColor: apt.member_color,
                                  borderLeftWidth: '3px'
                                }),
                                ...(apt.is_task && {
                                  backgroundColor: '#FEF3C7',
                                  borderColor: '#F59E0B'
                                })
                              }}
                            >
                              <div className="font-semibold truncate flex items-center gap-1">
                                {apt.is_task && <span className="text-amber-600">📋</span>}
                                {apt.requested_time || apt.time} - {apt.is_task ? apt.title : (apt.client_name || apt.custom_client_name || 'RDV')}
                              </div>
                              <div className="truncate text-[10px] opacity-80">{apt.subject || apt.description}</div>
                              {apt.member_name && (
                                <div 
                                  className="truncate text-[9px] mt-0.5 font-medium"
                                  style={{ color: apt.is_task ? '#D97706' : apt.member_color }}
                                >
                                  {apt.member_name}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* MONTH VIEW */
          <div className="p-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 mb-2">
              {DAYS_FR.map(day => (
                <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {getMonthDays().map(({ date, isCurrentMonth }, idx) => {
                const isToday = date.toDateString() === new Date().toDateString();
                const dayAppointments = getAppointmentsForDay(date);
                
                return (
                  <div
                    key={idx}
                    className={cn(
                      "min-h-[100px] p-1 border rounded-lg transition-colors",
                      isCurrentMonth ? "bg-white" : "bg-slate-50",
                      isToday && "border-indigo-400 bg-indigo-50"
                    )}
                  >
                    <div className={cn(
                      "text-sm font-medium mb-1 w-6 h-6 rounded-full flex items-center justify-center",
                      isToday ? "bg-indigo-600 text-white" : isCurrentMonth ? "text-slate-700" : "text-slate-400"
                    )}>
                      {date.getDate()}
                    </div>
                    
                    <div className="space-y-0.5">
                      {dayAppointments.slice(0, 3).map(apt => (
                        <div
                          key={apt.id}
                          onClick={() => openAppointmentDetails(apt)}
                          className={cn(
                            "text-[10px] px-1 py-0.5 rounded truncate cursor-pointer transition-colors border",
                            apt.is_task ? "bg-amber-100 border-amber-300 text-amber-800" : getStatusColor(apt.status)
                          )}
                        >
                          <span className="font-medium">
                            {apt.is_task && "📋 "}
                            {apt.requested_time || apt.time}
                          </span> {apt.is_task ? apt.title : apt.client_name}
                        </div>
                      ))}
                      {dayAppointments.length > 3 && (
                        <div className="text-[10px] text-slate-500 px-1">
                          +{dayAppointments.length - 3} autres
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {appointments.length === 0 && !loading && (
          <div className="text-center py-12 text-slate-500">
            <Calendar className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p className="text-lg font-medium">Aucun RDV ou tâche planifiée</p>
            <p className="text-sm mt-1">Les demandes de RDV et tâches planifiées apparaîtront ici</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setShowSettingsDialog(true)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Configurer Google Calendar
            </Button>
          </div>
        )}
      </CardContent>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Paramètres du Calendrier</DialogTitle>
            <DialogDescription>
              Intégrez votre Google Calendar ou utilisez le calendrier intégré
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p className="font-medium mb-2">Comment obtenir l'URL d'intégration :</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>Ouvrez <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">Google Calendar</a></li>
                <li>Cliquez sur ⚙️ Paramètres</li>
                <li>Sélectionnez votre calendrier dans la liste à gauche</li>
                <li>Descendez jusqu'à "Intégrer le calendrier"</li>
                <li>Copiez l'URL sous "URL publique de l'agenda" ou le code iframe</li>
              </ol>
            </div>
            
            <div>
              <Label>URL d'intégration Google Calendar</Label>
              <Input
                value={calendarEmbedUrl}
                onChange={(e) => setCalendarEmbedUrl(e.target.value)}
                placeholder="https://calendar.google.com/calendar/embed?src=..."
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Laissez vide pour utiliser le calendrier intégré
              </p>
            </div>

            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => window.open('https://calendar.google.com/calendar/r/settings', '_blank')}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Ouvrir les paramètres Google Calendar
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={saveCalendarSettings} 
              disabled={savingSettings}
              style={{ backgroundColor: theme?.colors?.accent }}
              className="text-white"
            >
              {savingSettings && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Appointment Details Dialog */}
      <Dialog open={showAppointmentDialog} onOpenChange={(open) => {
        setShowAppointmentDialog(open);
        if (!open) setIsEditing(false);
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5" style={{ color: theme?.colors?.accent }} />
                {isEditing ? 'Modifier le RDV' : 'Détails du RDV'}
              </span>
              {!isEditing && (isAgency || isProjectManager) && (
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setIsEditing(true)}
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                </div>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedAppointment && !isEditing && (
            <div className="space-y-4">
              {/* Status badge */}
              <div className="flex items-center justify-between">
                <Badge className={cn("text-sm px-3 py-1", getStatusColor(selectedAppointment.status))}>
                  {getStatusLabel(selectedAppointment.status)}
                </Badge>
                <span className="text-sm text-slate-500">
                  ID: {selectedAppointment.id?.slice(-6)}
                </span>
              </div>

              {/* Main info card */}
              <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg" style={{ color: theme?.colors?.primary }}>
                      {selectedAppointment.client_name}
                    </p>
                    {selectedAppointment.client_email && (
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {selectedAppointment.client_email}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="font-medium">{selectedAppointment.requested_date}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span className="font-medium">{selectedAppointment.requested_time}</span>
                  </div>
                </div>
              </div>

              {/* Subject and description */}
              <div className="space-y-2">
                <div>
                  <Label className="text-xs text-slate-500">Sujet</Label>
                  <p className="font-medium">{selectedAppointment.subject}</p>
                </div>
                {selectedAppointment.description && (
                  <div>
                    <Label className="text-xs text-slate-500">Description</Label>
                    <p className="text-sm text-slate-600 bg-white p-2 rounded border">
                      {selectedAppointment.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Proposed new time (if any) */}
              {selectedAppointment.status === 'proposed_new_time' && selectedAppointment.proposed_date && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-700">Nouvelle date proposée:</p>
                  <p className="text-sm text-blue-600">
                    {selectedAppointment.proposed_date} à {selectedAppointment.proposed_time}
                  </p>
                  {selectedAppointment.response_message && (
                    <p className="text-xs text-blue-500 mt-1 italic">
                      "{selectedAppointment.response_message}"
                    </p>
                  )}
                </div>
              )}

              {/* Response actions (only for pending appointments for this manager) */}
              {selectedAppointment.status === 'pending' && selectedAppointment.account_manager_id === user?.id && (
                <div className="space-y-3 pt-2 border-t">
                  <Label className="text-sm font-medium">Répondre à cette demande</Label>
                  
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={respondingTo === 'accept' ? 'default' : 'outline'}
                      onClick={() => setRespondingTo('accept')}
                      className={cn(respondingTo === 'accept' && 'bg-green-500 hover:bg-green-600 text-white')}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Accepter
                    </Button>
                    <Button
                      size="sm"
                      variant={respondingTo === 'propose' ? 'default' : 'outline'}
                      onClick={() => setRespondingTo('propose')}
                      className={cn(respondingTo === 'propose' && 'bg-blue-500 hover:bg-blue-600 text-white')}
                    >
                      <Calendar className="w-4 h-4 mr-1" />
                      Proposer autre
                    </Button>
                    <Button
                      size="sm"
                      variant={respondingTo === 'decline' ? 'default' : 'outline'}
                      onClick={() => setRespondingTo('decline')}
                      className={cn(respondingTo === 'decline' && 'bg-red-500 hover:bg-red-600 text-white')}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Décliner
                    </Button>
                  </div>

                  {respondingTo === 'propose' && (
                    <div className="grid grid-cols-2 gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div>
                        <Label className="text-xs">Nouvelle date</Label>
                        <Input 
                          type="date" 
                          value={proposedDate} 
                          onChange={(e) => setProposedDate(e.target.value)} 
                          className="h-9 mt-1" 
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Nouvelle heure</Label>
                        <Input 
                          type="time" 
                          value={proposedTime} 
                          onChange={(e) => setProposedTime(e.target.value)} 
                          className="h-9 mt-1" 
                        />
                      </div>
                    </div>
                  )}

                  {(respondingTo === 'propose' || respondingTo === 'decline') && (
                    <Textarea
                      value={responseMessage}
                      onChange={(e) => setResponseMessage(e.target.value)}
                      placeholder="Message au client (optionnel)..."
                      rows={2}
                      className="mt-2"
                    />
                  )}

                  {respondingTo && (
                    <Button
                      className="w-full mt-2 text-white"
                      onClick={() => handleRespondToAppointment(
                        selectedAppointment.id,
                        respondingTo === 'propose' ? 'propose_new_time' : respondingTo
                      )}
                      style={{ backgroundColor: theme?.colors?.accent }}
                    >
                      Confirmer la réponse
                    </Button>
                  )}
                </div>
              )}

              {/* Client reschedule request - Only for clients with accepted appointments */}
              {isClient && selectedAppointment.status === 'accepted' && selectedAppointment.client_id === user?.id && !showRescheduleRequest && (
                <div className="pt-3 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowRescheduleRequest(true);
                      setRescheduleForm({
                        requested_date: selectedAppointment.requested_date || '',
                        requested_time: selectedAppointment.requested_time || '',
                        reason: ''
                      });
                    }}
                    className="w-full"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Demander une modification de date
                  </Button>
                </div>
              )}

              {/* Reschedule request form */}
              {showRescheduleRequest && (
                <div className="space-y-3 pt-3 border-t">
                  <Label className="text-sm font-medium">Demande de modification</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Nouvelle date souhaitée</Label>
                      <Input
                        type="date"
                        value={rescheduleForm.requested_date}
                        onChange={(e) => setRescheduleForm({...rescheduleForm, requested_date: e.target.value})}
                        className="h-9 mt-1"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Nouvelle heure souhaitée</Label>
                      <Input
                        type="time"
                        value={rescheduleForm.requested_time}
                        onChange={(e) => setRescheduleForm({...rescheduleForm, requested_time: e.target.value})}
                        className="h-9 mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Motif (optionnel)</Label>
                    <Textarea
                      value={rescheduleForm.reason}
                      onChange={(e) => setRescheduleForm({...rescheduleForm, reason: e.target.value})}
                      placeholder="Expliquez pourquoi vous souhaitez modifier ce RDV..."
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowRescheduleRequest(false)}
                      className="flex-1"
                    >
                      Annuler
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleRequestReschedule}
                      disabled={sendingReschedule}
                      className="flex-1 text-white"
                      style={{ backgroundColor: theme?.colors?.accent }}
                    >
                      {sendingReschedule && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                      Envoyer la demande
                    </Button>
                  </div>
                </div>
              )}

              {/* Pending reschedule indicator */}
              {selectedAppointment.status === 'pending_reschedule' && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm font-medium text-amber-700 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Demande de modification en attente
                  </p>
                  {selectedAppointment.reschedule_requested_date && (
                    <p className="text-sm text-amber-600 mt-1">
                      Date souhaitée: {selectedAppointment.reschedule_requested_date} à {selectedAppointment.reschedule_requested_time}
                    </p>
                  )}
                  {selectedAppointment.reschedule_reason && (
                    <p className="text-xs text-amber-500 mt-1 italic">
                      "{selectedAppointment.reschedule_reason}"
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Edit Mode Form */}
          {selectedAppointment && isEditing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={editForm.requested_date}
                    onChange={(e) => setEditForm({...editForm, requested_date: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Heure</Label>
                  <Input
                    type="time"
                    value={editForm.requested_time}
                    onChange={(e) => setEditForm({...editForm, requested_time: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Sujet</Label>
                <Input
                  value={editForm.subject}
                  onChange={(e) => setEditForm({...editForm, subject: e.target.value})}
                  placeholder="Sujet du RDV"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  placeholder="Description..."
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Durée (minutes)</Label>
                <Input
                  type="number"
                  value={editForm.duration_minutes}
                  onChange={(e) => setEditForm({...editForm, duration_minutes: parseInt(e.target.value) || 30})}
                  min={15}
                  step={15}
                  className="mt-1 w-32"
                />
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteAppointment}
                >
                  <X className="w-4 h-4 mr-1" />
                  Supprimer
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsEditing(false)}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={handleSaveAppointment}
                    disabled={savingEdit}
                    style={{ backgroundColor: theme?.colors?.accent }}
                    className="text-white"
                  >
                    {savingEdit && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Enregistrer
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick RDV Creation Dialog */}
      <Dialog open={showQuickRdvDialog} onOpenChange={setShowQuickRdvDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" style={{ color: theme?.colors?.accent }} />
              Nouveau RDV
            </DialogTitle>
            <DialogDescription>
              Créer un rendez-vous pour le {quickRdvForm.date ? new Date(quickRdvForm.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="rdv-date">Date</Label>
                <Input
                  id="rdv-date"
                  type="date"
                  value={quickRdvForm.date}
                  onChange={(e) => setQuickRdvForm({ ...quickRdvForm, date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="rdv-time">Heure</Label>
                <Input
                  id="rdv-time"
                  type="time"
                  value={quickRdvForm.time}
                  onChange={(e) => setQuickRdvForm({ ...quickRdvForm, time: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Subject */}
            <div>
              <Label htmlFor="rdv-subject">Sujet *</Label>
              <Input
                id="rdv-subject"
                value={quickRdvForm.subject}
                onChange={(e) => setQuickRdvForm({ ...quickRdvForm, subject: e.target.value })}
                placeholder="Ex: Point projet, Appel découverte..."
                className="mt-1"
              />
            </div>

            {/* Client selection */}
            <div>
              <Label htmlFor="rdv-client">Client (optionnel)</Label>
              <select
                id="rdv-client"
                value={quickRdvForm.client_id}
                onChange={(e) => setQuickRdvForm({ ...quickRdvForm, client_id: e.target.value })}
                className="mt-1 w-full border rounded-md p-2 text-sm"
              >
                <option value="">-- Sans client --</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.company_name || `${c.first_name} ${c.last_name}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom client name (if no client selected) */}
            {!quickRdvForm.client_id && (
              <div>
                <Label htmlFor="rdv-custom-name">Nom du contact (si externe)</Label>
                <Input
                  id="rdv-custom-name"
                  value={quickRdvForm.custom_client_name}
                  onChange={(e) => setQuickRdvForm({ ...quickRdvForm, custom_client_name: e.target.value })}
                  placeholder="Ex: Jean Dupont (Prospect)"
                  className="mt-1"
                />
              </div>
            )}

            {/* Type */}
            <div>
              <Label htmlFor="rdv-type">Type</Label>
              <select
                id="rdv-type"
                value={quickRdvForm.appointment_type}
                onChange={(e) => setQuickRdvForm({ ...quickRdvForm, appointment_type: e.target.value })}
                className="mt-1 w-full border rounded-md p-2 text-sm"
              >
                <option value="meeting">RDV en agence</option>
                <option value="call">Call visio</option>
                <option value="phone">Appel téléphonique</option>
              </select>
            </div>

            {/* Duration */}
            <div>
              <Label htmlFor="rdv-duration">Durée</Label>
              <select
                id="rdv-duration"
                value={quickRdvForm.duration_minutes}
                onChange={(e) => setQuickRdvForm({ ...quickRdvForm, duration_minutes: parseInt(e.target.value) })}
                className="mt-1 w-full border rounded-md p-2 text-sm"
              >
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>1 heure</option>
                <option value={90}>1h30</option>
                <option value={120}>2 heures</option>
              </select>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="rdv-description">Notes (optionnel)</Label>
              <Textarea
                id="rdv-description"
                value={quickRdvForm.description}
                onChange={(e) => setQuickRdvForm({ ...quickRdvForm, description: e.target.value })}
                placeholder="Détails supplémentaires..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickRdvDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreateQuickRdv}
              disabled={!quickRdvForm.subject.trim() || savingRdv}
              style={{ backgroundColor: theme?.colors?.accent }}
              className="text-white"
            >
              {savingRdv && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Créer le RDV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default FullCalendar;
